/**
 * The browser's side of apps/server. `fetch` is passed in so tests can use a fake server. Every call sends the session cookie
 * (`credentials: "include"`) and, for changes, the `X-Requested-With: zookeepbit` header the server requires against CSRF.
 */
import type { FriendBookEntry } from "@zookeepbit/sim";

export type AccountErrorCode =
  | "invalid_profile"
  | "bad_code"
  | "unknown_code"
  | "too_many_friends"
  | "book_private"
  | "not_found"
  | "invalid_username"
  | "invalid_password"
  | "password_too_common"
  | "invalid_birth"
  | "cannot_create_account"
  | "username_taken"
  | "bad_credentials"
  | "too_many_attempts"
  | "not_signed_in"
  | "stale_save"
  | "invalid_save"
  | "busy"
  | "already_linked"
  | "not_needed"
  | "google_unavailable"
  | "invalid_link"
  | "wrong_mode"
  | "payments_unavailable"
  | "purchase_not_allowed"
  | "unknown_item"
  | "already_owned"
  | "no_pending"
  | "try_again"
  | "offline"
  | "server_error";

export class AccountError extends Error {
  constructor(
    readonly code: AccountErrorCode,
    readonly retryAfterSeconds?: number,
  ) {
    super(code);
  }
}

export interface ApiAccount {
  username: string;
  ageBand: string;
  createdAt: string;
}

export interface CloudSave {
  envelope: unknown;
  revision: number;
}

export interface Birth {
  year: number;
  month: number;
}

export interface AccountApi {
  register(username: string, password: string, birth: Birth): Promise<ApiAccount>;
  login(username: string, password: string): Promise<ApiAccount>;
  logout(): Promise<void>;
  /** The signed-in account, or null when nobody is signed in. */
  me(): Promise<(ApiAccount & { hasSave: boolean; google: boolean; approval: Approval; hasPassword: boolean }) | null>;
  /** The address of Google's sign-in (no account needed); the caller navigates to it. The way back is `?google=signedin|pending|error`. */
  startGoogleSignIn(): Promise<string>;
  /** After `?google=pending`: the age gate, which creates the account (with a generated username) and signs in. */
  completeGoogle(birth: Birth): Promise<ApiAccount>;
  /** Deleting a Google account (it has no password): the player types the username. */
  deleteGoogleAccount(confirmUsername: string): Promise<void>;
  /** The address of Google's sign-in (or the fake one) that links a Google account; the caller navigates to it. */
  startGoogle(): Promise<string>;
  /** A link for a parent (minor accounts only), good for 30 minutes. */
  requestApproval(): Promise<{ url: string; expiresAt: string }>;
  /** Who may buy: Google linked, and for a minor a parent's approval. */
  purchase(): Promise<PurchaseGate>;
  /** For the parents' page: works without a session, and says nothing about the child. */
  lookupApproval(token: string): Promise<{ valid: false } | { valid: true; kind: "approve" | "withdraw" }>;
  /** The address of the parent's own Google sign-in for this link. */
  startApprovalSignIn(token: string, mode: "approve" | "withdraw"): Promise<string>;
  /** What is for sale, with exact prices. Works without signing in. `testMode`: no real money moves. */
  shop(): Promise<{ testMode: boolean; items: ShopItem[] }>;
  /** The shop items the account has paid for (`kind:index`). */
  purchases(): Promise<string[]>;
  /** The address of the payment page for one item; the caller navigates to it. */
  checkout(itemId: string): Promise<string>;
  /** A promo code from the owner: gives the item for free. Throws `bad_code`, `already_owned` or `too_many_attempts`. */
  redeemPromo(code: string): Promise<{ itemId: string }>;
  /** Anonymous usage counts: no cookie, no account. */
  sendEvents(events: { name: string; n: number }[]): Promise<void>;
  /** The cloud save, or null when there is none. */
  getSave(): Promise<CloudSave | null>;
  /** `revision` is the one last seen (null to create the first save). Throws `stale_save` when someone else changed it. */
  putSave(envelope: unknown, revision: number | null): Promise<{ revision: number }>;
  exportData(): Promise<string>;
  deleteAccount(password: string): Promise<void>;
  getProfile(): Promise<Profile>;
  putProfile(choice: { nameIndex: number; avatarIndex: number; bookShared: boolean }): Promise<Profile>;
  /** Replaces the friend code; the old one stops working, friends stay. Resolves to the new code. */
  rotateCode(): Promise<string>;
  listFriends(): Promise<Friend[]>;
  /** `unknown_code` for a code that does not exist, is malformed or is the player's own (the server never says which). */
  addFriend(code: string): Promise<Friend>;
  /** `book_private` while the friend has not shared it, `not_found` when not friends. */
  getFriendBook(id: string): Promise<FriendBookEntry[]>;
  removeFriend(id: string): Promise<void>;
}

export interface ShopItem {
  id: string;
  name: string;
  /** Exactly what the player gets. */
  description: string;
  amountCents: number;
  currency: string;
}

export type Approval ="not_needed" | "missing" | "approved";

export interface PurchaseGate {
  canBuy: boolean;
  missing: ("google" | "parent")[];
}

export interface Profile {
  friendCode: string;
  nameIndex: number;
  avatarIndex: number;
  bookShared: boolean;
}

export interface Friend {
  /** Opaque, for this friend only; never the account id. */
  id: string;
  nameIndex: number;
  avatarIndex: number;
  bookShared: boolean;
  /** 0 while the book is private. */
  bookCount: number;
}

const KNOWN: ReadonlySet<string> = new Set<AccountErrorCode>([
  "invalid_profile",
  "bad_code",
  "unknown_code",
  "too_many_friends",
  "book_private",
  "not_found",
  "invalid_username",
  "invalid_password",
  "password_too_common",
  "invalid_birth",
  "cannot_create_account",
  "username_taken",
  "bad_credentials",
  "too_many_attempts",
  "not_signed_in",
  "stale_save",
  "invalid_save",
  "busy",
  "already_linked",
  "not_needed",
  "google_unavailable",
  "invalid_link",
  "wrong_mode",
  "payments_unavailable",
  "purchase_not_allowed",
  "unknown_item",
  "already_owned",
  "no_pending",
  "try_again",
]);

/** `VITE_API_URL` if set; the local server on localhost (so tests and dev never reach production); otherwise the real API. */
export function resolveBaseUrl(configured: string | undefined, hostname: string): string {
  if (configured) return configured.replace(/\/+$/, "");
  if (hostname === "localhost" || hostname === "127.0.0.1") return "http://localhost:3000";
  return "https://api.zookeepbit.com";
}

export function createApi(options: { fetch: typeof fetch; baseUrl: string }): AccountApi {
  const { baseUrl } = options;

  async function call(method: string, path: string, init: { body?: unknown; headers?: Record<string, string>; anonymous?: boolean } = {}): Promise<Response> {
    const headers: Record<string, string> = { ...init.headers };
    if (method !== "GET") headers["X-Requested-With"] = "zookeepbit";
    let body: string | undefined;
    if (init.body !== undefined) {
      body = JSON.stringify(init.body);
      headers["Content-Type"] = "application/json";
    }
    let res: Response;
    try {
      res = await options.fetch(baseUrl + path, { method, headers, body, credentials: init.anonymous ? "omit" : "include" });
    } catch {
      throw new AccountError("offline");
    }
    return res;
  }

  async function fail(res: Response): Promise<never> {
    let code = "server_error";
    try {
      const data = (await res.json()) as { error?: unknown };
      if (typeof data.error === "string") code = data.error;
    } catch {
      // No JSON body: keep the generic code.
    }
    if (res.status === 503 && code !== "google_unavailable" && code !== "payments_unavailable") code = "busy";
    const retry = Number(res.headers.get("Retry-After"));
    throw new AccountError((KNOWN.has(code) ? code : "server_error") as AccountErrorCode, Number.isFinite(retry) && retry > 0 ? retry : undefined);
  }

  async function account(res: Response): Promise<ApiAccount> {
    if (!res.ok) return fail(res);
    return ((await res.json()) as { account: ApiAccount }).account;
  }

  return {
    register: async (username, password, birth) => account(await call("POST", "/auth/register", { body: { username, password, birth } })),
    login: async (username, password) => account(await call("POST", "/auth/login", { body: { username, password } })),
    async logout() {
      const res = await call("POST", "/auth/logout");
      if (!res.ok) await fail(res);
    },
    async me() {
      const res = await call("GET", "/me");
      if (res.status === 401) return null;
      if (!res.ok) return fail(res);
      return (await res.json()) as ApiAccount & { hasSave: boolean; google: boolean; approval: Approval; hasPassword: boolean };
    },
    async startGoogleSignIn() {
      const res = await call("POST", "/auth/google/start");
      if (!res.ok) return fail(res);
      return ((await res.json()) as { url: string }).url;
    },
    completeGoogle: async (birth) => account(await call("POST", "/auth/google/complete", { body: { birth } })),
    async deleteGoogleAccount(confirmUsername) {
      const res = await call("DELETE", "/me", { body: { confirmUsername } });
      if (!res.ok) await fail(res);
    },
    async redeemPromo(code) {
      const res = await call("POST", "/me/promo/redeem", { body: { code } });
      if (!res.ok) return fail(res);
      return (await res.json()) as { itemId: string };
    },
    async sendEvents(events) {
      const res = await call("POST", "/events", { body: { events }, anonymous: true });
      if (!res.ok) await fail(res);
    },
    async startGoogle() {
      const res = await call("POST", "/me/google/start");
      if (!res.ok) return fail(res);
      return ((await res.json()) as { url: string }).url;
    },
    async requestApproval() {
      const res = await call("POST", "/me/approval");
      if (!res.ok) return fail(res);
      return (await res.json()) as { url: string; expiresAt: string };
    },
    async purchase() {
      const res = await call("GET", "/me/purchase");
      if (!res.ok) return fail(res);
      return (await res.json()) as PurchaseGate;
    },
    async lookupApproval(token) {
      const res = await call("GET", `/parent/approval?token=${encodeURIComponent(token)}`);
      if (!res.ok) return fail(res);
      return (await res.json()) as { valid: false } | { valid: true; kind: "approve" | "withdraw" };
    },
    async shop() {
      const res = await call("GET", "/shop");
      if (!res.ok) return fail(res);
      return (await res.json()) as { testMode: boolean; items: ShopItem[] };
    },
    async purchases() {
      const res = await call("GET", "/me/purchases");
      if (!res.ok) return fail(res);
      return ((await res.json()) as { owned: string[] }).owned;
    },
    async checkout(itemId) {
      const res = await call("POST", "/me/checkout", { body: { itemId } });
      if (!res.ok) return fail(res);
      return ((await res.json()) as { url: string }).url;
    },
    async startApprovalSignIn(token, mode) {
      const res = await call("POST", "/parent/approval/start", { body: { token, mode } });
      if (!res.ok) return fail(res);
      return ((await res.json()) as { url: string }).url;
    },
    async getSave() {
      const res = await call("GET", "/me/save");
      if (res.status === 404) return null;
      if (!res.ok) return fail(res);
      const revision = Number(/^"(\d+)"$/.exec(res.headers.get("ETag") ?? "")?.[1]);
      if (!Number.isInteger(revision)) throw new AccountError("server_error");
      return { envelope: ((await res.json()) as { save: unknown }).save, revision };
    },
    async putSave(envelope, revision) {
      const res = await call("PUT", "/me/save", { body: { save: envelope }, headers: revision === null ? {} : { "If-Match": `"${revision}"` } });
      if (!res.ok) return fail(res);
      return (await res.json()) as { revision: number };
    },
    async exportData() {
      const res = await call("GET", "/me/export");
      if (!res.ok) return fail(res);
      return res.text();
    },
    async deleteAccount(password) {
      const res = await call("DELETE", "/me", { body: { password } });
      if (!res.ok) await fail(res);
    },
    async getProfile() {
      const res = await call("GET", "/me/profile");
      if (!res.ok) return fail(res);
      return (await res.json()) as Profile;
    },
    async putProfile(choice) {
      const res = await call("PUT", "/me/profile", { body: choice });
      if (!res.ok) return fail(res);
      return (await res.json()) as Profile;
    },
    async rotateCode() {
      const res = await call("POST", "/me/profile/code");
      if (!res.ok) return fail(res);
      return ((await res.json()) as { friendCode: string }).friendCode;
    },
    async listFriends() {
      const res = await call("GET", "/me/friends");
      if (!res.ok) return fail(res);
      return ((await res.json()) as { friends: Friend[] }).friends;
    },
    async addFriend(code) {
      const res = await call("POST", "/me/friends", { body: { code } });
      if (!res.ok) return fail(res);
      return ((await res.json()) as { friend: Friend }).friend;
    },
    async getFriendBook(id) {
      const res = await call("GET", `/me/friends/${encodeURIComponent(id)}/book`);
      if (!res.ok) return fail(res);
      return ((await res.json()) as { entries: FriendBookEntry[] }).entries;
    },
    async removeFriend(id) {
      const res = await call("DELETE", `/me/friends/${encodeURIComponent(id)}`);
      if (!res.ok) await fail(res);
    },
  };
}

export function browserApi(): AccountApi {
  const configured = (import.meta.env as Record<string, string | undefined>).VITE_API_URL;
  return createApi({ fetch: (input, init) => window.fetch(input, init), baseUrl: resolveBaseUrl(configured, window.location.hostname) });
}
